import { useTranslation } from "react-i18next";
import { FileSpreadsheet, User, UsersRound } from "lucide-react";
import type { CampaignAudienceSnapshotSummary, CampaignAudiencePreviewResponse } from "@multi-chat/shared";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { ContactGroupPreview } from "../contacts/ContactGroupPreview.js";
import { CampaignAudienceIssuesList } from "./CampaignAudienceIssuesList.js";
import { previewMetricRows, summaryReasonEntries } from "./campaigns-presentation.js";

const METRIC_HELP_KEYS = {
  selectedSourceEntries: "campaigns.preview.metricHelp.selectedSourceEntries",
  canonicalRecipients: "campaigns.preview.metricHelp.canonicalRecipients",
  readyRecipients: "campaigns.preview.metricHelp.readyRecipients",
  excludedBeforeRecipient: "campaigns.preview.metricHelp.excludedBeforeRecipient",
  duplicateSourceEntries: "campaigns.preview.metricHelp.duplicateSourceEntries",
} as const satisfies Record<
  ReturnType<typeof previewMetricRows>[number]["key"],
  string
>;

const COMPACT_METRIC_ORDER = [
  "readyRecipients",
  "selectedSourceEntries",
  "duplicateSourceEntries",
  "excludedBeforeRecipient",
] as const;

export function CampaignAudiencePreview({
  preview,
  emptyReady,
  contactCount = 0,
  groups = [],
  csvFileName = null,
}: {
  preview: CampaignAudiencePreviewResponse;
  emptyReady: boolean;
  contactCount?: number;
  groups?: ReadonlyArray<{ id: number; name: string }>;
  csvFileName?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reasons = summaryReasonEntries(preview.summary.byReason);

  return (
    <section className="grid gap-4" data-testid="campaign-audience-preview">
      <AudienceReviewMetrics summary={preview.summary} />
      <div className="grid min-w-0 gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <h4 className="m-0 text-[0.8125rem] font-semibold">{t("campaigns.preview.sourcesTitle")}</h4>
          <SimpleTooltip content={t("campaigns.preview.dedupeHelp")}>
            <span className="cursor-help text-[0.7rem] text-subtle underline decoration-dotted">
              {t("campaigns.preview.dedupeNote")}
            </span>
          </SimpleTooltip>
        </div>
        <ul
          className="m-0 grid min-w-0 list-none grid-cols-[minmax(0,1fr)] divide-y divide-line/60 p-0 text-[0.8125rem]"
          data-testid="campaign-audience-review-sources"
        >
          <li className="flex min-w-0 items-center gap-2 py-1.5 first:pt-0">
            <User size={14} className="shrink-0 text-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1">{t("campaigns.sources.contacts")}</span>
            <span className="shrink-0 tabular-nums text-muted">
              {t("campaigns.sources.contactsSelected", { count: contactCount })}
            </span>
          </li>
          <li className="grid min-w-0 gap-1.5 py-1.5" data-testid="campaign-audience-review-groups">
            <div className="flex min-w-0 items-center gap-2">
              <UsersRound size={14} className="shrink-0 text-muted" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t("campaigns.sources.groups")}</span>
              <span className="shrink-0 tabular-nums text-muted">
                {t("campaigns.sources.groupsCount", { count: groups.length })}
              </span>
            </div>
            <ContactGroupPreview groups={groups} collapsible className="ps-[1.375rem]" />
          </li>
          <li className="flex min-w-0 items-center gap-2 py-1.5">
            <FileSpreadsheet size={14} className="shrink-0 text-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              {csvFileName ?? t("campaigns.sources.noCsv")}
            </span>
          </li>
        </ul>
      </div>
      {reasons.length > 0 ? (
        <div>
          <h4 className="m-0 mb-1.5 text-[0.8125rem] font-semibold">{t("campaigns.preview.issues")}</h4>
          <CampaignAudienceIssuesList reasons={reasons} samples={preview.issueSamples} />
        </div>
      ) : null}
      {preview.summary.byReason.DUPLICATE_RECIPIENT_CONFLICT ? (
        <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.csv.conflictHelp")}</p>
      ) : null}
      {emptyReady ? (
        <p className="m-0 text-[0.8125rem] text-error" role="alert">
          {t("campaigns.preview.zeroReady")}
        </p>
      ) : null}
    </section>
  );
}

export function AudienceReviewMetrics({
  summary,
}: {
  summary: CampaignAudienceSnapshotSummary;
}): React.JSX.Element {
  const { t } = useTranslation();
  const byKey = new Map(previewMetricRows(summary).map((row) => [row.key, row.value]));

  return (
    <div
      className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4"
      data-testid="campaign-audience-review-metrics"
    >
      {COMPACT_METRIC_ORDER.map((key) => {
        const value = byKey.get(key) ?? 0;
        const testId = key === "readyRecipients" ? "campaign-audience-final-count" : undefined;
        return (
          <div key={key} data-testid={testId} title={t(METRIC_HELP_KEYS[key])}>
            <p className="m-0 text-[0.7rem] font-medium text-muted">
              {t(`campaigns.preview.metrics.${key}`)}
            </p>
            <p className="m-0 text-[0.9375rem] font-semibold tabular-nums tracking-tight">
              {value}
            </p>
          </div>
        );
      })}
    </div>
  );
}
